// 12 bilingual notifications per demo organisation, 4 unread. Texts are built from the
// same fixtures the pages show (containers, sensors, orders, runs), so they agree.

import {
  DEMO_CONTAINERS,
  DEMO_DRIVERS,
  DEMO_OUTLET_ORG_ID,
  DEMO_RECYCLER_ORG_ID,
  DEMO_ROUTES,
  DEMO_SENSORS,
  DEMO_TRUCKS,
  HOUR_MS,
  ids,
  iso,
  isoDate,
  materialById,
  siteById,
} from '../core';
import type { CreateNotificationRequest, Notification, NotificationType } from '../../types/notification.type';
import { allRouteRuns, todaysRuns } from './fleet';
import { findApproval } from './approvals';
import { allPickupOrders, orderKg } from './pickupOrders';
import { sum } from './helpers';

type Row = [hoursAgo: number, read: boolean, type: NotificationType, titleEn: string, titleAr: string, msgEn: string, msgAr: string, data: Record<string, unknown>];

const approval = (n: number) => ({ notification_kind: 'approval', approval_request_id: ids.approval(n) });
const fmt = (n: number, digits = 0) => n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const tonnes = (kg: number) => fmt(kg / 1000, 1);

/** Fields of the order an approval request is about (set from the picked order). */
function approvalOrder(n: number): { site: string; city: string; material: string; amount: number; corrected: number | null } {
  const p = findApproval(ids.approval(n))?.payload ?? {};
  return {
    site: typeof p.collection_site === 'string' ? p.collection_site : 'the branch',
    city: typeof p.city === 'string' ? p.city : 'Riyadh',
    material: typeof p.material === 'string' ? p.material : 'recyclables',
    amount: typeof p.amount === 'number' ? p.amount : 0,
    corrected: typeof p.corrected_amount === 'number' ? p.corrected_amount : null,
  };
}
const CITY_AR: Record<string, string> = { Riyadh: 'الرياض', Jeddah: 'جدة' };

function outletRows(): Row[] {
  const orders = allPickupOrders();
  const weigh = approvalOrder(2);
  const sensored = DEMO_CONTAINERS.filter((c) => c.sensor_id && DEMO_SENSORS.find((s) => s.id === c.sensor_id)?.status !== 'offline');
  const fullest = [...sensored].sort((a, b) => b.fill_level - a.fill_level)[0];
  const fullSite = siteById(fullest.site_id);
  const fullMat = materialById(fullest.material_id);
  const lowBattery = DEMO_SENSORS.find((s) => s.status === 'low_battery');
  const lowContainer = DEMO_CONTAINERS.find((c) => c.id === lowBattery?.container_id);
  const offline = DEMO_SENSORS.find((s) => s.status === 'offline');
  const offContainer = DEMO_CONTAINERS.find((c) => c.id === offline?.container_id);
  const offSite = offline ? siteById(offline.site_id) : undefined;
  const offHours = offline ? Math.round((Date.now() - Date.parse(offline.last_seen_at)) / HOUR_MS) : 0;
  const transit = orders.filter((o) => o.status === 'in_transit').slice(-1)[0];
  const transitTruck = DEMO_TRUCKS.find((t) => t.id === transit?.truck_id);
  const transitSite = transit ? siteById(transit.site_id) : undefined;
  const transitMat = transit ? materialById(transit.material_id) : undefined;
  const lastDone = orders.filter((o) => o.status === 'completed').sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''))[0];
  const doneSite = siteById(lastDone.site_id);
  const doneMat = materialById(lastDone.material_id);
  const doneH = Math.max(1, Math.round((Date.now() - Date.parse(lastDone.completed_at ?? lastDone.created_at)) / HOUR_MS));
  const weekAgo = Date.now() - 7 * 24 * HOUR_MS;
  const week = orders.filter((o) => o.status === 'completed' && Date.parse(o.completed_at ?? '') >= weekAgo - 7 * 24 * HOUR_MS && Date.parse(o.completed_at ?? '') < weekAgo);
  const monthAgo = Date.now() - 30 * 24 * HOUR_MS;
  const credited = sum(orders.filter((o) => o.approval_status === 'approved' && Date.parse(o.completed_at ?? '') >= monthAgo - 30 * 24 * HOUR_MS && Date.parse(o.completed_at ?? '') < monthAgo), (o) => o.sale_amount_sar ?? 0);
  return [
    [0.6, false, 'push', 'Approval requested', 'طلب موافقة جديد', `Khalid Al-Otaibi asked to correct a ${weigh.material} pickup weight at ${weigh.site}.`, `طلب خالد العتيبي تصحيح وزن طلب استلام (${weigh.material}) في ${weigh.site}.`, approval(2)],
    [2.5, false, 'in_app', 'Container almost full', 'الحاوية شبه ممتلئة', `${fullest.code} (${fullMat?.name_en}) at ${fullSite?.name_en} is ${fullest.fill_level}% full.`, `الحاوية ${fullest.code} (${fullMat?.name_ar}) في ${fullSite?.name_ar} ممتلئة بنسبة ${fullest.fill_level}٪.`, { notification_kind: 'container', container_id: fullest.id }],
    [5, false, 'push', 'Pickup on its way', 'الشاحنة في الطريق', `Truck ${transitTruck?.plate_number ?? ''} is collecting ${transitMat?.name_en ?? 'recyclables'} from ${transitSite?.name_en ?? 'your branch'}.`, `الشاحنة ${transitTruck?.plate_number ?? ''} تستلم ${transitMat?.name_ar ?? 'المواد'} من ${transitSite?.name_ar ?? 'الفرع'}.`, { notification_kind: 'pickup_order', pickup_order_id: transit?.id }],
    [21, false, 'in_app', 'New user awaiting approval', 'مستخدم جديد بانتظار الموافقة', 'Faisal Al-Qahtani requested access for Lama Al-Qahtani (Olaya Flagship).', 'طلب فيصل القحطاني إضافة لمى القحطاني (فرع العليا الرئيسي).', approval(1)],
    [doneH, true, 'push', 'Pickup completed', 'اكتمل الاستلام', `${fmt(orderKg(lastDone))} kg of ${doneMat?.name_en} collected from ${doneSite?.name_en} (${lastDone.reference}).`, `تم استلام ${fmt(orderKg(lastDone))} كجم من ${doneMat?.name_ar} من ${doneSite?.name_ar} (${lastDone.reference}).`, { notification_kind: 'pickup_order', pickup_order_id: lastDone.id }],
    [49, true, 'in_app', 'Sensor battery low', 'بطارية المستشعر منخفضة', `The fill sensor on ${lowContainer?.code} reports ${lowBattery?.battery_pct}% battery.`, `مستشعر التعبئة في ${lowContainer?.code} يبلغ عن بطارية ${lowBattery?.battery_pct}٪.`, { notification_kind: 'sensor' }],
    [7 * 24 + 2, true, 'email', 'Weekly collection summary', 'ملخص الاستلام الأسبوعي', `Last week: ${week.length} pickups completed, ${tonnes(sum(week, orderKg))} t collected.`, `الأسبوع الماضي: ${week.length} طلبات استلام مكتملة، ${tonnes(sum(week, orderKg))} طن.`, { notification_kind: 'report' }],
    [Math.max(offHours - 6, 1), true, 'push', 'Sensor offline', 'المستشعر غير متصل', `The sensor on ${offContainer?.code} at ${offSite?.name_en} stopped reporting.`, `توقف مستشعر ${offContainer?.code} في ${offSite?.name_ar} عن الإرسال.`, { notification_kind: 'sensor' }],
    [130, true, 'in_app', 'Request rejected', 'تم رفض الطلب', "Abdullah Al-Shehri's request to delete a collected pickup was rejected by Noura Al-Harbi.", 'رفضت نورة الحربي طلب عبدالله الشهري حذف طلب استلام مكتمل.', approval(9)],
    [170, true, 'email', 'Monthly ESG report ready', 'تقرير الاستدامة الشهري جاهز', 'The branch waste report for last month is ready to download.', 'تقرير نفايات الفروع للشهر الماضي جاهز للتنزيل.', { notification_kind: 'report' }],
    [30 * 24 + 4, true, 'push', 'Revenue credited', 'تم إيداع الإيراد', `SAR ${fmt(credited, 2)} credited for last month's approved pickups.`, `تم إيداع ${fmt(credited, 2)} ريال عن طلبات الشهر الماضي المعتمدة.`, { notification_kind: 'invoice' }],
    [24 * 120, true, 'in_app', 'Welcome to Recyclee Zero', 'مرحباً بك في ريسايكلي زيرو', 'Your branches, containers and pickups are now connected.', 'تم ربط فروعك وحاوياتك وطلبات الاستلام.', { notification_kind: 'system' }],
  ];
}

function recyclerRows(): Row[] {
  const contaminated = approvalOrder(103);
  const scale = approvalOrder(102);
  const rollback = approvalOrder(109);
  const live = todaysRuns().find((r) => r.status === 'in_progress');
  const liveRoute = DEMO_ROUTES.find((r) => r.id === live?.route_id);
  const liveTruck = DEMO_TRUCKS.find((t) => t.id === live?.truck_id);
  const done = live ? live.stops.filter((s) => s.status === 'completed').length : 0;
  const yesterday = isoDate(new Date(Date.now() - 24 * HOUR_MS));
  const yRuns = allRouteRuns().filter((r) => r.run_date === yesterday);
  const yKg = sum(yRuns, (r) => sum(r.stops, (s) => s.collected_kg ?? 0));
  const lastMonth = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth() - 1, 1)).toISOString().slice(0, 7);
  const lmRuns = allRouteRuns().filter((r) => r.run_date.startsWith(lastMonth));
  const lmStops = sum(lmRuns, (r) => r.stops.length);
  const soonest = [...DEMO_DRIVERS].sort((a, b) => a.licence_expiry.localeCompare(b.licence_expiry))[0];
  const expiryDays = Math.max(0, Math.round((Date.parse(`${soonest.licence_expiry}T00:00:00Z`) - Date.now()) / (24 * HOUR_MS)));
  const pending = allPickupOrders().filter((o) => o.status === 'pending').slice(-1)[0];
  const pSite = siteById(pending.site_id);
  const pMat = materialById(pending.material_id);
  const weekStart = Date.now() - 7 * 24 * HOUR_MS;
  const newRequests = allPickupOrders().filter((o) => Date.parse(o.created_at) >= weekStart);
  const newKg = sum(newRequests, orderKg);
  return [
    [0.4, false, 'push', 'Route in progress', 'الجولة قيد التنفيذ', `${liveTruck?.plate_number} finished stop ${done} of ${live?.stops.length ?? 0} on the ${liveRoute?.name_en}.`, `أنهت الشاحنة ${liveTruck?.plate_number} المحطة ${done} من ${live?.stops.length ?? 0} في ${liveRoute?.name_ar}.`, { notification_kind: 'route_run', route_run_id: live?.id }],
    [3, false, 'push', 'Approval requested', 'طلب موافقة جديد', `Ahmed Al-Juhani wants to reject a contaminated ${contaminated.material} load from ${contaminated.site}.`, `يطلب أحمد الجهني رفض حمولة ملوثة (${contaminated.material}) من ${contaminated.site}.`, approval(103)],
    [16, false, 'in_app', 'Weight correction awaiting you', 'تصحيح وزن بانتظارك', `Step 2 of 2: confirm the ${fmt(scale.corrected ?? scale.amount)} kg gate-scale reading for ${scale.material}.`, `الخطوة 2 من 2: تأكيد قراءة الميزان ${fmt(scale.corrected ?? scale.amount)} كجم (${scale.material}).`, approval(102)],
    [29, false, 'in_app', 'New driver awaiting approval', 'سائق جديد بانتظار الموافقة', 'Mona Al-Fahad requested access for Waleed Al-Harthy (Jeddah).', 'طلبت منى الفهد إضافة وليد الحارثي (جدة).', approval(101)],
    [31, true, 'push', 'Truck in maintenance', 'الشاحنة في الصيانة', 'LJA 6640 is booked in for brake service at the Sulay workshop.', 'الشاحنة LJA 6640 في الصيانة لفحص المكابح في ورشة السلي.', { notification_kind: 'truck' }],
    [20, true, 'email', 'Daily route summary', 'ملخص الجولات اليومي', `Yesterday ${yRuns.length} routes collected ${tonnes(yKg)} t from ${sum(yRuns, (r) => r.stops.length)} stops.`, `أمس نفذت ${yRuns.length} جولات واستلمت ${tonnes(yKg)} طن من ${sum(yRuns, (r) => r.stops.length)} محطة.`, { notification_kind: 'report' }],
    [76, true, 'in_app', 'Pickup requested', 'طلب استلام جديد', `Al Nakheel Markets raised a ${pMat?.name_en} pickup at ${pSite?.name_en}.`, `أنشأت منافذ النخيل طلب استلام ${pMat?.name_ar} في ${pSite?.name_ar}.`, { notification_kind: 'pickup_order', pickup_order_id: pending.id }],
    [100, true, 'push', 'Licence renewal due', 'تجديد الرخصة مستحق', `${soonest.full_name}'s driving licence expires in ${expiryDays + 4} days.`, `تنتهي رخصة القيادة لـ${soonest.full_name_ar} خلال ${expiryDays + 4} يوماً.`, { notification_kind: 'driver', driver_id: soonest.id }],
    [150, true, 'in_app', 'Request approved', 'تمت الموافقة على الطلب', `The pickup rollback in ${rollback.city} (${rollback.site}) was approved.`, `تمت الموافقة على إعادة فتح طلب الاستلام في ${CITY_AR[rollback.city] ?? rollback.city} (${rollback.site}).`, approval(109)],
    [200, true, 'email', 'Monthly operations report', 'تقرير العمليات الشهري', `Your fleet ran ${lmRuns.length} routes last month and collected at all ${lmStops} scheduled stops.`, `نفذ أسطولك ${lmRuns.length} جولة الشهر الماضي وتم الاستلام من جميع المحطات (${lmStops}).`, { notification_kind: 'report' }],
    [8, true, 'in_app', 'Supply this week', 'التوريد هذا الأسبوع', `Al Nakheel Markets raised ${newRequests.length} pickup requests this week (about ${tonnes(newKg)} t).`, `أنشأت منافذ النخيل ${newRequests.length} طلب استلام هذا الأسبوع (حوالي ${tonnes(newKg)} طن).`, { notification_kind: 'pickup_order' }],
    [24 * 150, true, 'in_app', 'Welcome to Recyclee Zero', 'مرحباً بك في ريسايكلي زيرو', 'Your fleet, routes and suppliers are now connected.', 'تم ربط أسطولك وجولاتك ومورديك.', { notification_kind: 'system' }],
  ];
}

function build(rows: Row[], offset: number): Notification[] {
  return rows.map(([h, read, type, title_en, title_ar, message_en, message_ar, data], i) => ({
    id: ids.notification(offset + i + 1),
    title_en,
    title_ar,
    message_en,
    message_ar,
    type,
    data,
    is_read: read,
    created_at: iso(new Date(Date.now() - h * HOUR_MS)),
  }));
}

let byOrg: Record<string, Notification[]> = {};
let createdCount = 0;

export function resetNotifications(): void {
  byOrg = {
    [DEMO_OUTLET_ORG_ID]: build(outletRows(), 0),
    [DEMO_RECYCLER_ORG_ID]: build(recyclerRows(), 100),
  };
  createdCount = 0;
}

resetNotifications();

export function notificationsFor(orgId: string, limit?: unknown): { result_list: Notification[]; total_count: number } {
  const list = [...(byOrg[orgId] ?? [])].sort((a, b) => b.created_at.localeCompare(a.created_at));
  const n = Number(limit);
  return { result_list: Number.isFinite(n) && n > 0 ? list.slice(0, n) : list, total_count: list.length };
}

export function unreadCount(orgId: string): number {
  return (byOrg[orgId] ?? []).filter((n) => !n.is_read).length;
}

export function markRead(orgId: string, id: string): Notification | null {
  const list = byOrg[orgId] ?? [];
  const hit = list.find((n) => n.id === id);
  if (!hit) return null;
  const next = { ...hit, is_read: true };
  byOrg = { ...byOrg, [orgId]: list.map((n) => (n.id === id ? next : n)) };
  return next;
}

export function createNotification(body: CreateNotificationRequest): Notification {
  createdCount += 1;
  const n: Notification = {
    id: ids.notification(500 + createdCount),
    title_en: body.title_en,
    title_ar: body.title_ar,
    message_en: body.message_en,
    message_ar: body.message_ar,
    type: body.type,
    data: body.data ?? {},
    is_read: false,
    created_at: iso(new Date()),
  };
  const orgId = body.organization_id;
  byOrg = { ...byOrg, [orgId]: [n, ...(byOrg[orgId] ?? [])] };
  return n;
}
